/**
 * The button look, separated from the button so a router `<Link>` can wear it.
 * A link that navigates must stay a link — wrapped in a `<button>`, middle-click
 * and "open in new tab" quietly stop working — so both need the class list, and
 * only one of them is a component.
 *
 * Ported from `zms_license_server/web/src/components/Button.tsx` with the new
 * palette (docs/09-theme.md): the green primary becomes `--azure-deep`.
 */

export type ButtonVariant = "primary" | "ghost" | "destructive" | "quiet";
export type ButtonSize = "sm" | "md";

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: "bg-azure-deep text-on-accent hover:opacity-90",
  ghost: "border border-line bg-chip-bg text-ink-soft hover:bg-link-bg-hover hover:text-ink",
  destructive: "border border-line bg-chip-bg text-ink-soft hover:bg-link-bg-hover",
  quiet: "text-ink-soft hover:bg-link-bg-hover hover:text-ink",
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  // 24px is the WCAG 2.2 minimum target and these land above it; the primary
  // actions on touch get their height from the page, not from here.
  sm: "px-2.5 py-1.5 text-sm",
  md: "px-4 py-2 text-base",
};

export function buttonClass(variant: ButtonVariant = "primary", size: ButtonSize = "md"): string {
  return [
    "inline-flex items-center justify-center gap-1.5 rounded-lg font-semibold transition",
    "disabled:cursor-not-allowed disabled:opacity-50",
    VARIANT_CLASSES[variant],
    SIZE_CLASSES[size],
    // Destructive intent is stated on hover rather than at rest: a row of red
    // buttons makes the dangerous one harder to spot, not easier.
    variant === "destructive" ? "hover:text-[var(--chart-crit)]" : "",
  ]
    .filter(Boolean)
    .join(" ");
}
